// Knowledge Base loader. All `docs/**/*.md` files in the monorepo are bundled
// into the admin build at compile time via import.meta.glob — no backend, no
// runtime fetch. Doc updates require redeploying the admin (acceptable for an
// internal KB).

export type DocSection =
  | 'customer'
  | 'driver'
  | 'admin'
  | '_shared'
  | 'engineering'
  | 'operations'
  | 'integrations'
  | 'brand'

export type DocFrontmatter = {
  title: string
  app: string
  audience: string
  status: 'stable' | 'draft' | 'pending-integration'
  updated: string
  tags: string[]
}

export type Doc = {
  slug: string          // e.g. "driver/order-queue"
  section: DocSection
  filename: string      // e.g. "order-queue"
  frontmatter: DocFrontmatter
  body: string          // markdown without the frontmatter block
}

const KNOWN_SECTIONS: readonly DocSection[] = [
  'customer',
  'driver',
  'admin',
  '_shared',
  'engineering',
  'operations',
  'integrations',
  'brand',
]
const KNOWN_SECTION_SET = new Set<string>(KNOWN_SECTIONS)

// Vite bundles every match as a raw string at build time. The path is relative
// from THIS file; admin/src/lib → ../../../docs.
const modules = import.meta.glob('../../../docs/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

// Minimal YAML parser tuned to our frontmatter shape:
//   key: value         (string)
//   key: [a, b, c]     (string array)
// No nesting, no quotes-handling beyond stripping a single surrounding pair.
function parseFrontmatter(raw: string): { fm: DocFrontmatter; body: string } {
  const m = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/)
  const empty: DocFrontmatter = { title: '', app: '', audience: '', status: 'draft', updated: '', tags: [] }
  if (!m) return { fm: empty, body: raw }
  const [, fmRaw, body] = m
  const fm: Record<string, string | string[]> = {}
  for (const rawLine of fmRaw.split(/\r?\n/)) {
    const line = rawLine.trim()
    if (!line || line.startsWith('#')) continue
    const colon = line.indexOf(':')
    if (colon < 0) continue
    const key = line.slice(0, colon).trim()
    let val: string | string[] = line.slice(colon + 1).trim()
    if (val.startsWith('[') && val.endsWith(']')) {
      val = val.slice(1, -1).split(',').map(s => s.trim()).filter(Boolean)
    } else {
      // Strip surrounding quotes
      if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
        val = val.slice(1, -1)
      }
    }
    fm[key] = val
  }
  return {
    fm: {
      title: String(fm.title || ''),
      app: String(fm.app || ''),
      audience: String(fm.audience || ''),
      status: (fm.status as DocFrontmatter['status']) || 'draft',
      updated: String(fm.updated || ''),
      tags: Array.isArray(fm.tags) ? fm.tags : [],
    },
    body,
  }
}

export const docs: Doc[] = Object.entries(modules)
  .map(([path, raw]) => {
    // path looks like '../../../docs/driver/order-queue.md'
    const m = path.match(/\/docs\/([^/]+)\/(.+)\.md$/)
    if (!m) return null
    const [, section, filename] = m
    if (!KNOWN_SECTION_SET.has(section)) return null
    const { fm, body } = parseFrontmatter(raw)
    return {
      slug: `${section}/${filename}`,
      section: section as DocSection,
      filename,
      frontmatter: fm,
      body,
    }
  })
  .filter((d): d is Doc => d !== null)
  .sort((a, b) => a.frontmatter.title.localeCompare(b.frontmatter.title))

type AdminRole = 'super_admin' | 'operations' | 'accounting' | 'marketing'

// Per-section role gate. A role sees a section if its name appears in
// `roles`. Sections without a `roles` field are visible to everyone.
//   - Customer / Driver / Admin / Shared: visible to ALL admin roles
//     (any admin may need to look up a screen or policy).
//   - Operations Runbooks: ops + super_admin (decision trees for daily work).
//   - Integrations: super_admin + ops (vendor diagnostic info; ops handles
//     SMS / FCM / Resend incidents).
//   - Brand & Style: super_admin + marketing (style guide for promo content).
//   - Engineering: super_admin only (architecture / schema / security).
export const SECTION_META: Record<DocSection, { label: string; order: number; roles?: AdminRole[] }> = {
  customer: { label: 'Customer App', order: 1 },
  driver: { label: 'Ambassador App', order: 2 },
  admin: { label: 'Admin Portal', order: 3 },
  _shared: { label: 'Cross-App Policies', order: 4 },
  operations: { label: 'Operations Runbooks', order: 5, roles: ['super_admin', 'operations'] },
  integrations: { label: 'Third-Party Integrations', order: 6, roles: ['super_admin', 'operations'] },
  brand: { label: 'Brand & Style', order: 7, roles: ['super_admin', 'marketing'] },
  engineering: { label: 'Engineering', order: 8, roles: ['super_admin'] },
}

// Returns true if `role` should see the given section in the sidebar.
export function canSeeSection(section: DocSection, role: string | undefined): boolean {
  const allowed = SECTION_META[section].roles
  if (!allowed) return true
  return !!role && allowed.includes(role as AdminRole)
}

export function docsBySection(): Array<{ section: DocSection; label: string; items: Doc[] }> {
  const groups = new Map<DocSection, Doc[]>()
  for (const d of docs) {
    const arr = groups.get(d.section) ?? []
    arr.push(d)
    groups.set(d.section, arr)
  }
  // Sort sections by SECTION_META.order so the sidebar layout is intentional
  // (Customer/Driver/Admin/Shared first; Engineering last for ops audiences).
  return KNOWN_SECTIONS
    .filter(s => groups.has(s))
    .sort((a, b) => SECTION_META[a].order - SECTION_META[b].order)
    .map(s => ({ section: s, label: SECTION_META[s].label, items: groups.get(s)! }))
}

export function getDoc(section: string, filename: string): Doc | undefined {
  return docs.find(d => d.section === section && d.filename === filename)
}

export function searchDocs(query: string): Doc[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return docs.filter(d =>
    d.frontmatter.title.toLowerCase().includes(q) ||
    d.frontmatter.tags.some(t => t.toLowerCase().includes(q)) ||
    d.body.toLowerCase().includes(q),
  )
}

// Resolve a relative .md link found in a doc body to a KB route (or null if
// it points outside the Knowledge Base — e.g. SNAGS.md, memory files).
export function resolveDocLink(currentDoc: Doc, href: string): string | null {
  const [pathPart] = href.split('#')
  if (!pathPart.endsWith('.md')) return null
  try {
    const base = new URL(`https://kb.local/${currentDoc.section}/${currentDoc.filename}.md`)
    const resolved = new URL(pathPart, base)
    const m = resolved.pathname.match(/^\/([^/]+)\/(.+)\.md$/)
    if (!m) return null
    const [, section, filename] = m
    if (!KNOWN_SECTION_SET.has(section)) return null
    if (!getDoc(section, filename)) return null
    return `/kb/${section}/${filename}`
  } catch {
    return null
  }
}
